import { useState, useRef, useEffect, useCallback } from 'react'
import { Send, Bot, User, Loader2 } from 'lucide-react'
import { meetingsAPI } from '../../services/api'
import { decodeHtmlEntities } from '../../utils/displayText'

/**
 * Chat with Rithmo panel for assistant_external meetings.
 * Sends messages via POST /api/meetings/:meetingId/chat-with-rithmo
 * and receives responses via the dashboard WebSocket (RITHMO_CHAT_RESPONSE frames).
 *
 * @param {{ meetingId: string, isLive: boolean, wsRef?: React.MutableRefObject<WebSocket|null> }} props
 */
export default function ChatWithRithmoPanel({ meetingId, isLive, wsRef }) {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const seenIdsRef = useRef(new Set())

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Listen for RITHMO_CHAT_RESPONSE on the dashboard WebSocket.
  // User messages are added optimistically by handleSend — only render non-user WS messages.
  const handleWsMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data)
      if (data.type === 'RITHMO_CHAT_RESPONSE' && data.meetingId === meetingId && data.payload) {
        const { id, message, role, timestamp } = data.payload
        if (role === 'user') return // already added optimistically
        if (seenIdsRef.current.has(id)) return
        seenIdsRef.current.add(id)
        setMessages((prev) => [...prev, { id, text: message, role, timestamp }])
      }
    } catch {
      // ignore non-JSON frames
    }
  }, [meetingId])

  useEffect(() => {
    const ws = wsRef?.current
    if (!ws) return
    ws.addEventListener('message', handleWsMessage)
    return () => ws.removeEventListener('message', handleWsMessage)
  }, [wsRef, handleWsMessage])

  // Re-attach listener when WS reconnects (wsRef.current changes)
  useEffect(() => {
    if (!wsRef) return
    let lastWs = wsRef.current
    if (lastWs) lastWs.addEventListener('message', handleWsMessage)
    const interval = setInterval(() => {
      const ws = wsRef.current
      if (ws && ws !== lastWs) {
        if (lastWs) lastWs.removeEventListener('message', handleWsMessage)
        ws.addEventListener('message', handleWsMessage)
        lastWs = ws
      }
    }, 2000)
    return () => {
      clearInterval(interval)
      if (lastWs) lastWs.removeEventListener('message', handleWsMessage)
    }
  }, [wsRef, handleWsMessage])

  const handleSend = async () => {
    const text = input.trim()
    if (!text || sending) return
    setInput('')
    setSending(true)

    // Optimistically add the user message (WS echo will be deduped by seenIdsRef)
    const tempId = `local_${Date.now()}`
    seenIdsRef.current.add(tempId)
    setMessages((prev) => [...prev, { id: tempId, text, role: 'user', timestamp: Date.now() }])

    try {
      const res = await meetingsAPI.chatWithRithmo(meetingId, text)
      // If the server returns a messageId, register it so the WS echo is deduped
      if (res?.messageId) {
        seenIdsRef.current.add(res.messageId)
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { id: `err_${Date.now()}`, text: 'Failed to send message. Please try again.', role: 'system', timestamp: Date.now() },
      ])
    } finally {
      setSending(false)
      // Restore focus to the input after sending
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  if (!isLive) {
    return (
      <div className="glass rounded-2xl p-8 text-center">
        <Bot className="w-12 h-12 text-indigo-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Chat with Rithmo</h3>
        <p className="text-sm text-gray-500">
          Start or join the meeting to chat with Rithmo in real time. You can ask about decisions,
          action items, agenda status, and more.
        </p>
      </div>
    )
  }

  return (
    <div className="glass rounded-2xl flex flex-col" style={{ height: 'calc(100vh - 320px)', minHeight: '400px' }}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
          <Bot className="w-4 h-4 text-indigo-600" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Chat with Rithmo</h3>
          <p className="text-xs text-gray-500">Ask about the meeting in real time</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <Bot className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-400">
              Ask Rithmo anything about this meeting. Try:
            </p>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              {['What decisions have been made?', 'Any action items?', 'What was just discussed?'].map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => { setInput(suggestion); }}
                  className="text-xs px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.role !== 'user' && (
              <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Bot className="w-3.5 h-3.5 text-indigo-600" />
              </div>
            )}
            <div
              className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'bg-indigo-600 text-white rounded-br-md'
                  : msg.role === 'system'
                    ? 'bg-red-50 text-red-700 border border-red-200'
                    : 'bg-gray-100 text-gray-900 rounded-bl-md'
              }`}
            >
              {decodeHtmlEntities(msg.text)}
            </div>
            {msg.role === 'user' && (
              <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                <User className="w-3.5 h-3.5 text-gray-600" />
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-6 py-4 border-t border-gray-200">
        <div className="flex items-end gap-3">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Rithmo..."
            rows={1}
            className="flex-1 resize-none rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            style={{ maxHeight: '120px' }}
            disabled={sending}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || sending}
            className="p-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex-shrink-0"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
